import {
  buildPageProperties,
  getAbsoluteLink,
  getPricingPlan,
  getPricingPlanNewPricing,
  getPricingPlanMonthDuration,
  getPricingPlanTax,
  isInternalLink,
} from "./properties";
import { VisitorContextType } from "src/providers/VisitorContext/types";
import { ClickInfo, ClickInfoNewPricing } from "./types";
import { logEvent } from "../analytics";
import { navigate } from "gatsby";
import { getNewAccountEventName } from "./name";
import handleWebAppRedirection from "src/atoms/PrismicLink/lib/handleWebAppRedirection";

/**
 * Prepares the appropriate structure for the Segment event to be sent
 * @see https://segment.com/docs/connections/spec/track/
 */
const buildNewAccountClickedEvent = (link: string, clickInfo: ClickInfo) => {
  const { fromLocation, platform, label, price, title, block, subtitle } =
    clickInfo;

  const eventName = getNewAccountEventName(link, clickInfo);
  const hasPrice = typeof price !== "undefined";
  const absoluteLink = getAbsoluteLink(link);
  const plan = getPricingPlan(price, title);
  const months = getPricingPlanMonthDuration(price, title);
  const tax = getPricingPlanTax(subtitle);
  const propertiesBlock = block ? { block } : {};
  const propertiesCurrency = hasPrice ? { currency: "EUR" } : {};
  // When false, we still want to make it apparent
  const propertiesLabel = label ? { label } : {};
  const propertiesMonths = months ? { months } : {};
  const propertiesPlan = plan ? { plan } : {};
  const propertiesPlatform = platform ? { platform } : {};
  const propertiesPrice = hasPrice ? { value: price } : {};
  const propertiesTax = tax ? { tax } : {};
  const propertiesOther = fromLocation ? buildPageProperties(fromLocation) : {};

  const properties = {
    destinationUrl: absoluteLink,
    ...propertiesBlock,
    ...propertiesCurrency,
    ...propertiesLabel,
    ...propertiesMonths,
    ...propertiesPlan,
    ...propertiesPlatform,
    ...propertiesPrice,
    ...propertiesTax,
    ...propertiesOther,
  };

  const event = { name: eventName, properties };

  return event;
};

/**
 * Prepares the appropriate structure for the Segment event to be sent
 * @see https://segment.com/docs/connections/spec/track/
 */
const buildNewAccountClickedEventNewPricing = (
  link: string,
  clickInfoNewPricing: ClickInfoNewPricing,
) => {
  const {
    fromLocation,
    platform,
    label,
    block,
    plan: planName,
  } = clickInfoNewPricing;

  const absoluteLink = getAbsoluteLink(link);
  const plan = getPricingPlanNewPricing(fromLocation, planName);
  const propertiesBlock = block ? { block } : {};
  const propertiesLabel = label ? { label } : {};
  const propertiesPlan = plan ? { plan } : {};
  const propertiesPlatform = platform ? { platform } : {};
  const propertiesPath = fromLocation?.pathname
    ? { path: fromLocation.pathname }
    : {};

  const properties = {
    destinationUrl: absoluteLink,
    ...propertiesBlock,
    ...propertiesLabel,
    ...propertiesPlan,
    ...propertiesPlatform,
    ...propertiesPath,
  };

  const event = { name: "Lead New Pricing Button Clicked", properties };

  return event;
};

const onClickNewAccountNewPricing = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  clickInfoNewPricing: ClickInfoNewPricing,
) => {
  e.preventDefault();

  const link = clickInfoNewPricing.toUrl;
  const event = buildNewAccountClickedEventNewPricing(
    link,
    clickInfoNewPricing,
  );
  const isClientSideRedirection = isInternalLink(link);

  // We enhance the user experience by navigating immediately
  if (isClientSideRedirection) {
    logEvent(event);
    navigate(link);
  } else {
    logEvent(event, () => navigate(link));
  }
};

const onClickNewAccount = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  visitorContext: VisitorContextType,
  clickInfo: ClickInfo,
) => {
  e.preventDefault();

  const link = handleWebAppRedirection(visitorContext, {
    from: clickInfo.fromLocation,
  });
  const event = buildNewAccountClickedEvent(link, clickInfo);
  const isClientSideRedirection = isInternalLink(link);
  console.log("Redirection link:", link);

  // We enhance the user experience by navigating immediately
  if (isClientSideRedirection) {
    logEvent(event);
    navigate(link);
  } else {
    logEvent(event, () => navigate(link));
  }
};

const onClickStartWithLink = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  visitorContext: VisitorContextType,
  clickInfo: ClickInfo,
) => {
  e.preventDefault();

  const link = clickInfo?.toUrl as string;
  const event = buildNewAccountClickedEvent(link, clickInfo);
  const isClientSideRedirection = isInternalLink(link);

  // We enhance the user experience by navigating immediately
  if (isClientSideRedirection) {
    logEvent(event);
    navigate(link);
  } else {
    logEvent(event, () => navigate(link));
  }
};

/**
 * For now we will start only supporting `gclid` which is specific to Google Ads
 * In the future we could support many more ids specific to other platforms.
 * @returns gclid or `null` if does not exist
 */
const extractsGclid = (location: Location) => {
  const params = new URLSearchParams(location.search);
  const gclid = params.get("gclid");

  return gclid ? gclid : null;
};

const hasChangedLocation = (
  prevLocation: Location | null,
  location: Location,
) => {
  const SEARCH_PARAM_OFFER = "offre";
  const SEARCH_PARAM_SECTION = "section";

  const { pathname: prevPathname } = prevLocation || {};
  const { pathname, search } = location;

  const prevSearchParams = new URLSearchParams((prevLocation || {}).search);
  const prevOffer = prevSearchParams.get(SEARCH_PARAM_OFFER);
  const prevSection = prevSearchParams.get(SEARCH_PARAM_SECTION);

  // Gatsby relies on pathname changes by default so we need to override the logic
  // We do so by adding an allowlist of query params
  const searchParams = new URLSearchParams(search);
  const offer = searchParams.get(SEARCH_PARAM_OFFER);
  const section = searchParams.get(SEARCH_PARAM_SECTION);

  const isDifferentPathname = prevPathname !== pathname;
  const isDifferentOffer = prevOffer !== offer;
  const isDifferentSection = prevSection !== section;

  // For instance, /tarifs/ page has different content based on `section` query param
  const isDifferent =
    isDifferentPathname || isDifferentOffer || isDifferentSection;

  return isDifferent;
};

export {
  buildNewAccountClickedEvent,
  extractsGclid,
  hasChangedLocation,
  onClickNewAccount,
  onClickNewAccountNewPricing,
  onClickStartWithLink,
};
